/**
 * Favourites store — persisted to localStorage.
 */
import { useState, useCallback, useEffect } from 'react';

export interface FavouriteStop {
  id: string;
  name: string;
  parentName?: string;
  addedAt: string;
  sortOrder: number;
}

export interface SavedTrip {
  id: string;
  name: string;
  originId: string;
  originName: string;
  destId: string;
  destName: string;
  addedAt: string;
}

const STOPS_KEY = 'sydneypt_favourite_stops';
const TRIPS_KEY = 'sydneypt_saved_trips';

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, data: unknown) {
  localStorage.setItem(key, JSON.stringify(data));
}

// ═══ Favourite Stops ═══

export function getFavouriteStops(): FavouriteStop[] {
  return loadJson<FavouriteStop[]>(STOPS_KEY, []);
}

export function saveFavouriteStops(stops: FavouriteStop[]) {
  saveJson(STOPS_KEY, stops);
}

export function addFavouriteStop(stop: Omit<FavouriteStop, 'addedAt' | 'sortOrder'>) {
  const stops = getFavouriteStops();
  if (stops.some((s) => s.id === stop.id)) return stops;
  const next = [
    ...stops,
    { ...stop, addedAt: new Date().toISOString(), sortOrder: stops.length },
  ];
  saveFavouriteStops(next);
  return next;
}

export function removeFavouriteStop(stopId: string) {
  const stops = getFavouriteStops().filter((s) => s.id !== stopId);
  saveFavouriteStops(stops);
  return stops;
}

export function isFavouriteStop(stopId: string): boolean {
  return getFavouriteStops().some((s) => s.id === stopId);
}

// ═══ Saved Trips ═══

export function getSavedTrips(): SavedTrip[] {
  return loadJson<SavedTrip[]>(TRIPS_KEY, []);
}

export function saveSavedTrips(trips: SavedTrip[]) {
  saveJson(TRIPS_KEY, trips);
}

export function addSavedTrip(trip: Omit<SavedTrip, 'id' | 'addedAt'>) {
  const trips = getSavedTrips();
  const next = [
    ...trips,
    { ...trip, id: `trip_${Date.now()}`, addedAt: new Date().toISOString() },
  ];
  saveSavedTrips(next);
  return next;
}

export function removeSavedTrip(tripId: string) {
  const trips = getSavedTrips().filter((t) => t.id !== tripId);
  saveSavedTrips(trips);
  return trips;
}

// ═══ React Hooks ═══

export function useFavouriteStops() {
  const [stops, setStops] = useState<FavouriteStop[]>(getFavouriteStops);

  useEffect(() => {
    const handler = () => setStops(getFavouriteStops());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const add = useCallback((stop: Omit<FavouriteStop, 'addedAt' | 'sortOrder'>) => {
    const next = addFavouriteStop(stop);
    setStops(next);
  }, []);

  const remove = useCallback((stopId: string) => {
    const next = removeFavouriteStop(stopId);
    setStops(next);
  }, []);

  return { stops, add, remove };
}

export function useSavedTrips() {
  const [trips, setTrips] = useState<SavedTrip[]>(getSavedTrips);

  useEffect(() => {
    const handler = () => setTrips(getSavedTrips());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const add = useCallback((trip: Omit<SavedTrip, 'id' | 'addedAt'>) => {
    const next = addSavedTrip(trip);
    setTrips(next);
  }, []);

  const remove = useCallback((tripId: string) => {
    const next = removeSavedTrip(tripId);
    setTrips(next);
  }, []);

  return { trips, add, remove };
}
